import { SmsTemplates } from '../components/SmsTemplates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MAIL_RELAYS, RELAY_PRESETS } from '@jt/domain';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { api } from '../lib/api';
import type { SettingsPayload } from '../lib/types';
import { ScrollX } from '../components/ScrollX';

/**
 * Everything an admin can change without a deploy.
 *
 * Grouped by who is affected rather than by where the value is stored: how the
 * business runs, how jobs reach technicians, what customers are told, and the
 * accounts the messages go through. Someone changing the working day should not
 * have to scroll past an API token to find it.
 *
 * Credentials are write-only. What comes back is `••••7f3a`, and leaving that
 * alone keeps the stored value — so opening this page and pressing Save cannot
 * wipe the accounts.
 */
/** The relay presets, in the order they should be offered. */
const RELAY_CHOICES = MAIL_RELAYS.map((id) => RELAY_PRESETS[id]);

/**
 * What to call each role on a list of toggles.
 *
 * Written out rather than derived. Capitalising the role and adding an s read
 * fine for four of them and produced "Saless" for the fifth, which is the
 * usual fate of a pluralisation rule — and it went unnoticed because nothing
 * on that screen did anything, so nobody looked at it twice.
 */
const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrators',
  dispatcher: 'Dispatchers',
  technician: 'Technicians',
  sales: 'Sales',
  hr: 'HR',
  accountant: 'Accountants',
};

export function SettingsPage() {
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const [form, setForm] = useState<SettingsPayload | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data && !form) setForm(structuredClone(data));
  }, [data, form]);

  const save = useMutation({
    mutationFn: (patch: Partial<SettingsPayload>) => api.updateSettings(patch),
    onSuccess: async (fresh) => {
      setForm(structuredClone(fresh));
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 4000);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const testEmail = useMutation({ mutationFn: api.testEmail });

  /** A real send, to an address typed here. See the panel below the connection test. */
  const [testTo, setTestTo] = useState('');
  const sendTest = useMutation({ mutationFn: (to: string) => api.sendTestEmail(to) });

  if (isPending || !form) {
    return <Loader2 className="mx-auto mt-16 h-6 w-6 animate-spin text-primary-500" />;
  }

  const patch = <S extends keyof SettingsPayload>(section: S, value: Partial<SettingsPayload[S]>) =>
    setForm({ ...form, [section]: { ...form[section], ...value } });

  /** Operations settings sit one level deeper, so they need their own helper. */
  const patchOps = <S extends keyof SettingsPayload['operations']>(
    section: S,
    value: Partial<SettingsPayload['operations'][S]>,
  ) =>
    setForm({
      ...form,
      operations: {
        ...form.operations,
        [section]: { ...form.operations[section], ...value },
      },
    });

  const patchChannel = (
    channel: 'whatsapp' | 'sms' | 'email',
    value: Record<string, unknown>,
  ) =>
    setForm({
      ...form,
      notifications: {
        ...form.notifications,
        [channel]: { ...form.notifications[channel], ...value },
      },
    });

  return (
    <div className="space-y-5 pb-16">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl">Settings</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Changes take effect within a minute — no restart, no deploy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="inline-flex items-center gap-1.5 text-sm text-secondary-700">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
              Saved
            </span>
          )}
          <button
            type="button"
            className="btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate(form)}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {error && <p className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{error}</p>}

      {/* ── The business ───────────────────────────────────────────────── */}
      <Section title="Your business" note="Appears on every message a customer or technician receives.">
        <Grid>
          <Field label="Business name">
            <input
              className="input w-full"
              value={form.organisation.name}
              onChange={(e) => patch('organisation', { name: e.target.value })}
            />
          </Field>
          <Field label="Time zone" hint="Used for business hours and every SLA clock.">
            <input
              className="input w-full"
              value={form.organisation.timezone}
              onChange={(e) => patch('organisation', { timezone: e.target.value })}
            />
          </Field>
          <Field label="Support phone" hint="Shown to customers on their tracking page and in emails.">
            <input
              className="input w-full"
              value={form.organisation.supportPhone ?? ''}
              onChange={(e) => patch('organisation', { supportPhone: e.target.value })}
              placeholder="+254700000000"
            />
          </Field>
          <Field
            label="Alert address"
            hint="Where the system reports its own trouble: undelivered dispatches, a stalled queue, several SLA breaches at once."
          >
            <input
              type="email"
              className="input w-full"
              value={form.organisation.alertEmail ?? ''}
              onChange={(e) => patch('organisation', { alertEmail: e.target.value })}
              placeholder="you@gospeednet.co.ke"
            />
          </Field>
        </Grid>
      </Section>

      {/* ── Dispatch ───────────────────────────────────────────────────── */}
      <Section title="Dispatch" note="How jobs reach technicians.">
        <Grid>
          <Field
            label="Chase after (minutes)"
            hint="How long an assignment can go unacknowledged before the technician is reminded."
          >
            <input
              type="number"
              min={1}
              max={240}
              className="input w-full"
              value={form.dispatch.ackTimeoutMinutes}
              onChange={(e) => patch('dispatch', { ackTimeoutMinutes: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Job link valid for (hours)"
            hint="A technician's link stops working after this. It is revoked immediately on reassignment regardless."
          >
            <input
              type="number"
              min={1}
              max={8760}
              className="input w-full"
              value={form.dispatch.linkTtlHours}
              onChange={(e) => patch('dispatch', { linkTtlHours: Number(e.target.value) })}
            />
          </Field>
        </Grid>
        <Field
          label="Working day"
          hint="SLA clocks only run inside these hours, so an overnight fault does not eat its target before anyone is awake."
        >
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="time"
              className="input"
              value={form.businessHours.start}
              onChange={(e) => patch('businessHours', { start: e.target.value })}
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="time"
              className="input"
              value={form.businessHours.end}
              onChange={(e) => patch('businessHours', { end: e.target.value })}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, day) => (
              <button
                key={label}
                type="button"
                onClick={() =>
                  patch('businessHours', {
                    workingDays: form.businessHours.workingDays.includes(day)
                      ? form.businessHours.workingDays.filter((d) => d !== day)
                      : [...form.businessHours.workingDays, day].sort(),
                  })
                }
                className={`rounded-full border px-3 py-1 text-xs ${
                  form.businessHours.workingDays.includes(day)
                    ? 'border-primary-500 bg-primary-50 text-primary-800'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </Field>
      </Section>

      {/* ── Contracts ──────────────────────────────────────────────────── */}
      <Section
        title="Contracts"
        note="Who signs for the company, and how long the vault holds an agreement."
      >
        {/*
          Loud, because the fallback is deliberately unfinished. With no name
          set, every contract goes out signed by the literal words "Branch
          Manager" — obvious to whoever reads the first draft, and exactly the
          sort of thing that otherwise reaches a customer.
        */}
        {!form.contracts.signatoryName && (
          <div className="rounded-xl bg-accent-50 p-3 text-sm">
            <p className="font-medium text-accent-700">No signatory is set</p>
            <p className="mt-0.5 text-muted-foreground">
              Contracts will go out signed &ldquo;Branch Manager&rdquo; with no name. Put the
              real one in before sending anything to a customer.
            </p>
          </div>
        )}

        <Grid>
          <Field
            label="Who signs for the company"
            hint="Applied automatically the moment a contract is sent, so a customer never receives one with an empty company signature block."
          >
            <input
              className="input w-full"
              value={form.contracts.signatoryName ?? ''}
              onChange={(e) => patch('contracts', { signatoryName: e.target.value })}
              placeholder="Full name of the branch manager"
            />
          </Field>
          <Field label="Their title">
            <input
              className="input w-full"
              value={form.contracts.signatoryTitle}
              onChange={(e) => patch('contracts', { signatoryTitle: e.target.value })}
              placeholder="Branch Manager"
            />
          </Field>
          <Field label="Their email" hint="Recorded with the signature. Not shown to the customer.">
            <input
              type="email"
              className="input w-full"
              value={form.contracts.signatoryEmail ?? ''}
              onChange={(e) => patch('contracts', { signatoryEmail: e.target.value })}
            />
          </Field>
          <Field label="Branch, as printed on the contract">
            <input
              className="input w-full"
              value={form.contracts.branchName ?? ''}
              onChange={(e) => patch('contracts', { branchName: e.target.value })}
              placeholder="Kisii, Kisii County"
            />
          </Field>
          <Field label="Default term (months)">
            <input
              type="number"
              min={1}
              max={120}
              className="input w-full"
              value={form.contracts.defaultTermMonths}
              onChange={(e) =>
                patch('contracts', { defaultTermMonths: Number(e.target.value) })
              }
            />
          </Field>
          <Field
            label="Keep signed contracts for (years)"
            hint="A floor, not a ceiling — nothing deletes itself when it passes. Seven is what Kenyan tax records are held for, and the API refuses anything shorter."
          >
            <input
              type="number"
              min={7}
              max={50}
              className="input w-full"
              value={form.contracts.retentionYears}
              onChange={(e) => patch('contracts', { retentionYears: Number(e.target.value) })}
            />
          </Field>
        </Grid>

        <p className="text-xs text-muted-foreground">
          A signed contract can never be edited — not by a dispatcher, not by a salesperson, not
          by you. Correcting one raises a new version that the customer signs again, and the
          original stays readable with its own signatures. That is what makes it worth anything
          when a bank or a court asks.
        </p>
      </Section>

      {/* ── Drills ─────────────────────────────────────────────────────── */}
      <Section
        title="Drills"
        note="Practice quizzes — subnetting, fibre colours, optical budgets, PON, DNS and DHCP. They read nothing from the business and no score is recorded anywhere."
      >
        <div className="space-y-2">
          {(['admin', 'dispatcher', 'technician', 'sales', 'hr', 'accountant'] as const).map((role) => (
            <Toggle
              key={role}
              /*
               * Named, not pluralised by rule. Adding an s to each role read
               * fine until `sales` arrived and the screen offered a toggle
               * for "Saless" — a rule that is wrong for one of six cases is
               * not saving anybody anything.
               */
              label={ROLE_LABELS[role] ?? role}
              checked={form.drills.visibleToRoles.includes(role)}
              onChange={(v) =>
                patch('drills', {
                  visibleToRoles: v
                    ? [...form.drills.visibleToRoles, role]
                    : form.drills.visibleToRoles.filter((r) => r !== role),
                })
              }
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Everybody by default. Only an admin can change this. Turning a role off removes Drills
          from their menu, and tells anybody who has the address that it was switched off — it
          takes effect on their next page load, without them signing out.
        </p>
      </Section>

      {/* ── The assistant ──────────────────────────────────────────────── */}
      <Section
        title="Assistant"
        note="A question box in the top bar, on every screen. It answers from your own data and only ever shows a person what their account can already see."
      >
        <div className="space-y-2">
          <Toggle
            label="Show the assistant to everyone"
            hint="Off hides the button for every role, and the API refuses too — a hidden button is not a switch."
            checked={form.assistant.enabled}
            onChange={(v) => patch('assistant', { enabled: v })}
          />
          <Toggle
            label="Let it word replies with the AI model"
            hint="Only affects phrasing. Every figure is read from the database either way, so a wrong answer is a wrong question rather than an invented number."
            checked={form.assistant.useModel}
            onChange={(v) => patch('assistant', { useModel: v })}
          />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          It can answer: what is open, what has breached its SLA, what is unassigned, which jobs are
          still missing a technician report, who is free, and what stock was used. A technician sees
          only their own jobs and is told plainly when something is not theirs to see.
        </p>
      </Section>

      {/* ── Customers ──────────────────────────────────────────────────── */}
      <Section
        title="What customers are told"
        note="At most three messages per job, whatever is switched on here. A fourth trains people to ignore the third."
      >
        <div className="space-y-2">
          <Toggle
            label="When their fault is logged"
            checked={form.customerMessaging.onTicketLogged}
            onChange={(v) => patch('customerMessaging', { onTicketLogged: v })}
          />
          <Toggle
            label="When a technician is on the way"
            hint="The one that stops people phoning to ask."
            checked={form.customerMessaging.onTechnicianOnTheWay}
            onChange={(v) => patch('customerMessaging', { onTechnicianOnTheWay: v })}
          />
          <Toggle
            label="When the work is finished"
            checked={form.customerMessaging.onCompleted}
            onChange={(v) => patch('customerMessaging', { onCompleted: v })}
          />
          <Toggle
            label="Ask how the visit went"
            hint="Included in the completion message rather than sent separately."
            checked={form.customerMessaging.askForRating}
            onChange={(v) => patch('customerMessaging', { askForRating: v })}
          />
        </div>
      </Section>

      {/* ── Service targets ────────────────────────────────────────────── */}
      <Section
        title="Service targets"
        note="What you promise customers. Every SLA clock, warning and breach is measured against these."
      >
        <ScrollX>
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left">
              <tr>
                <th className="py-2 pr-4 font-semibold">Priority</th>
                <th className="py-2 pr-4 font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    Respond within
                    <HelpHint>
                      From the ticket being logged to a technician acknowledging it. Working hours
                      only, so an overnight fault does not eat its target before anyone is awake.
                    </HelpHint>
                  </span>
                </th>
                <th className="py-2 font-semibold">Resolve within</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(['p1', 'p2', 'p3', 'p4'] as const).map((key) => (
                <tr key={key}>
                  <td className="py-2 pr-4 font-medium uppercase">{key}</td>
                  <td className="py-2 pr-4">
                    <input
                      type="number"
                      min={1}
                      className="input w-28"
                      value={form.operations.sla[key].responseMin}
                      onChange={(e) =>
                        patchOps('sla', {
                          [key]: {
                            ...form.operations.sla[key],
                            responseMin: Number(e.target.value),
                          },
                        })
                      }
                    />
                    <span className="ml-2 text-xs text-muted-foreground">min</span>
                  </td>
                  <td className="py-2">
                    <input
                      type="number"
                      min={1}
                      className="input w-28"
                      value={form.operations.sla[key].resolutionMin}
                      onChange={(e) =>
                        patchOps('sla', {
                          [key]: {
                            ...form.operations.sla[key],
                            resolutionMin: Number(e.target.value),
                          },
                        })
                      }
                    />
                    <span className="ml-2 text-xs text-muted-foreground">min</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>

        <Field
          label="Warn at"
          hint="How much of the target may be used before a ticket appears under Needs attention. 0.75 means three quarters."
        >
          <input
            type="number"
            step="0.05"
            min={0.05}
            max={0.95}
            className="input w-28"
            value={form.operations.sla.warnAtFraction}
            onChange={(e) => patchOps('sla', { warnAtFraction: Number(e.target.value) })}
          />
        </Field>
      </Section>

      {/* ── Expenses and leave ─────────────────────────────────────────── */}
      <Section
        title="Expenses"
        note="Claiming is optional and unlimited unless you set a rule here. Both boxes are blank by default — an approver is already a person looking at each claim."
      >
        <Grid>
          <Field
            label="Receipt required above (KES)"
            hint="Leave blank to never require one. A lost receipt should not mean a real cost goes unrecorded."
          >
            <input
              type="number"
              min={0}
              className="input w-full"
              placeholder="No receipt required"
              // Blank rather than 0: an empty box reads as "no rule", where a 0
              // reads as "every claim needs a receipt" — the opposite.
              value={form.operations.expenses.receiptRequiredAboveKes ?? ''}
              onChange={(e) =>
                patchOps('expenses', {
                  receiptRequiredAboveKes: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </Field>
          <Field
            label="Refuse a single claim above (KES)"
            hint="Leave blank for no ceiling. Set one only if you want claims above it refused outright rather than reviewed."
          >
            <input
              type="number"
              min={0}
              className="input w-full"
              placeholder="No limit"
              value={form.operations.expenses.maxClaimKes ?? ''}
              onChange={(e) =>
                patchOps('expenses', {
                  maxClaimKes: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </Field>
        </Grid>

        <Field
          label="Categories"
          hint="A fixed list rather than free text, so a report can add them up. Separate with commas."
        >
          <input
            className="input w-full"
            value={form.operations.expenses.categories.join(', ')}
            onChange={(e) =>
              patchOps('expenses', {
                categories: e.target.value
                  .split(',')
                  .map((c) => c.trim().toLowerCase())
                  .filter(Boolean),
              })
            }
          />
        </Field>
      </Section>

      {/*
        Its own section rather than buried under messaging settings, because
        editing wording is a different job from configuring a provider — one
        is done by whoever writes to customers, the other by whoever holds the
        credentials, and they are rarely the same person.
      */}
      <Section
        title="SMS wording"
        note="What each message says, and what it will cost to send."
      >
        <SmsTemplates />
      </Section>

      <Section title="Leave" note="The kinds of leave people may ask for.">
        <Field label="Kinds of leave" hint="Separate with commas.">
          <input
            className="input w-full"
            value={form.operations.leave.types.join(', ')}
            onChange={(e) =>
              patchOps('leave', {
                types: e.target.value
                  .split(',')
                  .map((t) => t.trim().toLowerCase())
                  .filter(Boolean),
              })
            }
          />
        </Field>
      </Section>

      {/* ── Alerting ───────────────────────────────────────────────────── */}
      <Section
        title="When to alert you"
        note="The system emails your alert address when these are crossed. Set them high enough that a message means something."
      >
        <Grid>
          <Field
            label="Open SLA breaches"
            hint="One breach is a bad day. Several at once usually means an outage nobody has linked to an incident."
          >
            <input
              type="number"
              min={1}
              className="input w-full"
              value={form.operations.alerts.slaBreachThreshold}
              onChange={(e) => patchOps('alerts', { slaBreachThreshold: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Queue stalled after (minutes)"
            hint="The worker normally clears the queue in seconds, so anything sitting this long means it is not running."
          >
            <input
              type="number"
              min={1}
              className="input w-full"
              value={form.operations.alerts.queueStuckMinutes}
              onChange={(e) => patchOps('alerts', { queueStuckMinutes: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Repeat the same alert every (hours)"
            hint="An alert that arrives constantly gets filtered into a folder, and then the real one is missed."
          >
            <input
              type="number"
              min={1}
              className="input w-full"
              value={form.operations.alerts.repeatAfterHours}
              onChange={(e) => patchOps('alerts', { repeatAfterHours: Number(e.target.value) })}
            />
          </Field>
          <Field
            label="Most messages per job to a customer"
            hint="Three is logged, on the way, done. A fourth trains someone to ignore the third."
          >
            <input
              type="number"
              min={1}
              max={10}
              className="input w-full"
              value={form.customerMessaging.maxPerJob}
              onChange={(e) => patch('customerMessaging', { maxPerJob: Number(e.target.value) })}
            />
          </Field>
        </Grid>
      </Section>

      {/* ── Signing in with Google, GitHub or Microsoft ─────────────────── */}
      <Section
        title="Sign-in providers"
        note="Let people sign in with an account they already have. Nobody gets in without an admin approving them first."
      >
        <label className="flex min-h-tap items-start gap-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            checked={form.signIn.allowSelfSignup}
            onChange={(e) => patch('signIn', { allowSelfSignup: e.target.checked })}
          />
          <span className="text-sm">
            Let somebody without an account ask for one
            <span className="block text-xs text-muted-foreground">
              With this off, only people who already have an account here can use a provider to
              sign in. With it on, a stranger can raise a request for you to decide on — it never
              means they get in.
            </span>
          </span>
        </label>

        {(['google', 'github', 'microsoft'] as const).map((provider) => (
          <div key={provider} className="rounded-xl border border-border p-4">
            <label className="flex min-h-tap items-center gap-3">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.signIn[provider].enabled}
                onChange={(e) =>
                  patch('signIn', {
                    [provider]: { ...form.signIn[provider], enabled: e.target.checked },
                  } as Partial<SettingsPayload['signIn']>)
                }
              />
              <span className="text-sm font-medium">{PROVIDER_LABELS[provider]}</span>
              {/*
                Configured means both halves are present. Shown because an
                enabled provider with no credentials draws a button that leads
                to an error, and the admin who ticked the box is the only person
                who can tell the difference.
              */}
              {form.signIn[provider].clientId && form.signIn[provider].clientSecret ? (
                <span className="badge bg-navy-50 text-navy-700">configured</span>
              ) : (
                <span className="badge bg-muted text-muted-foreground">not set up</span>
              )}
            </label>

            <Grid>
              <Field
                label="Client ID"
                hint="From the provider's own console. Not a secret — it appears in the sign-in URL."
              >
                <input
                  className="input w-full"
                  value={form.signIn[provider].clientId ?? ''}
                  onChange={(e) =>
                    patch('signIn', {
                      [provider]: { ...form.signIn[provider], clientId: e.target.value },
                    } as Partial<SettingsPayload['signIn']>)
                  }
                  placeholder={PROVIDER_ID_HINT[provider]}
                />
              </Field>
              <Field
                label="Client secret"
                hint="Stored encrypted and never shown again. A dotted value means one is saved — leave it alone to keep it."
              >
                <input
                  type="password"
                  className="input w-full"
                  value={form.signIn[provider].clientSecret ?? ''}
                  onChange={(e) =>
                    patch('signIn', {
                      [provider]: { ...form.signIn[provider], clientSecret: e.target.value },
                    } as Partial<SettingsPayload['signIn']>)
                  }
                />
              </Field>
            </Grid>

            {/*
              The redirect URI, ready to copy.
              It has to match what is registered with the provider exactly —
              character for character, including the scheme and any trailing
              path — and a mismatch is the single most common reason a
              first-time setup fails, with an error message that names neither
              side. So the value is shown rather than described.
            */}
            <p className="mt-3 text-xs text-muted-foreground">
              Redirect URI to register with {PROVIDER_LABELS[provider]}:
            </p>
            <code className="mt-1 block overflow-x-auto rounded-lg bg-muted px-3 py-2 text-xs">
              {`${window.location.origin}/api/v1/auth/oauth/${provider}/callback`}
            </code>
          </div>
        ))}

        <p className="text-sm text-muted-foreground">
          Signing in this way never creates an account on its own. It creates a request, which
          appears under <strong>Groups and roles</strong> for you to approve with a role — and the
          person then has to confirm from a link emailed to them. Three steps, none of which they
          control.
        </p>
      </Section>

      {/* ── Providers ──────────────────────────────────────────────────── */}
      <Section
        title="Messaging accounts"
        note="Credentials are stored encrypted and never shown again. Leaving a dotted field alone keeps what is saved."
      >
        <Provider
          title="Email"
          live
          enabled={form.notifications.email.enabled}
          onToggle={(v) => patchChannel('email', { enabled: v })}
          fallback={form.environmentFallback.email}
        >
          <fieldset className="mb-4">
            <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">
              How email is sent
              <HelpHint>
                Your own relay is better in every way except setup time. FormSubmit needs no
                account and no DNS records, so it can send today — but the message arrives as a
                plain table from their address, not your designed email from yours.
              </HelpHint>
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                {
                  id: 'smtp',
                  label: 'Your own relay (SMTP)',
                  note: 'Recommended. Needs an account and DNS records.',
                },
                {
                  id: 'formsubmit',
                  label: 'FormSubmit',
                  note: 'No setup at all. Each address must confirm once before anything arrives.',
                },
              ].map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => patchChannel('email', { transport: option.id })}
                  className={`rounded-xl border p-3 text-left text-sm ${
                    form.notifications.email.transport === option.id
                      ? 'border-primary-500 bg-primary-50 text-primary-800'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                >
                  <span className="block font-medium">{option.label}</span>
                  <span className="block text-xs text-muted-foreground">{option.note}</span>
                </button>
              ))}
            </div>

            {form.notifications.email.transport === 'formsubmit' && (
              <div className="mt-3 rounded-xl bg-accent-50 p-3 text-sm text-accent-800">
                <p className="font-medium">Before this works, each address must confirm.</p>
                <p className="mt-1">
                  The first message to any new address makes FormSubmit email that person a
                  confirmation link instead. Until they click it, sends are recorded as failed and
                  nothing is delivered — you will see them under Failed deliveries with the reason.
                  Send one to each technician now and ask them to confirm.
                </p>
                <p className="mt-1">
                  Mail arrives from FormSubmit rather than your domain, there are no bounce
                  notices, and the designed layout is replaced by a plain table. Move to your own
                  relay when you have one.
                </p>
              </div>
            )}
          </fieldset>

          {form.notifications.email.transport === 'formsubmit' ? (
            <Grid>
              <Field label="Layout" hint="FormSubmit renders one of its own three layouts.">
                <select
                  className="input w-full"
                  value={form.notifications.email.formsubmitTemplate ?? 'table'}
                  onChange={(e) => patchChannel('email', { formsubmitTemplate: e.target.value })}
                >
                  <option value="table">Table</option>
                  <option value="box">Box</option>
                  <option value="basic">Basic</option>
                </select>
              </Field>
              <Field label="Reply-to" hint="Where a reply lands. Worth setting: replies to FormSubmit go nowhere.">
                <input
                  className="input w-full"
                  value={form.notifications.email.replyTo ?? ''}
                  onChange={(e) => patchChannel('email', { replyTo: e.target.value })}
                />
              </Field>
            </Grid>
          ) : (
          <>
          {/*
            Which relay, as a choice rather than a quiz about port numbers.

            The two boxes underneath were the whole interface before this, and
            they asked an administrator to know that Gmail is `smtp.gmail.com`
            on 587 — which is not a decision, it is recall. The presets carry
            the host and the port; the boxes stay for anything not listed.
          */}
          <fieldset className="mb-4">
            <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium">
              Which mail server
              <HelpHint>
                Sending through your own server means mail arrives from your own domain, so the
                SPF and DKIM records set up for gospeednet.co.ke actually vouch for it. Sent
                through Gmail it arrives from a gmail.com address whatever the From line says,
                and those records vouch for nothing.
              </HelpHint>
            </legend>

            <div className="space-y-2">
              {RELAY_CHOICES.map((choice) => {
                const active = (form.notifications.email.relay ?? 'own') === choice.id;
                return (
                  <label
                    key={choice.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                      active ? 'border-primary-500 bg-primary-50/50' : 'border-border hover:bg-muted/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="mail-relay"
                      className="mt-0.5 h-4 w-4 shrink-0"
                      checked={active}
                      onChange={() =>
                        patchChannel('email', {
                          relay: choice.id,
                          /*
                           * The typed host is cleared when a preset is chosen,
                           * so a stale `smtp.gmail.com` left in the box cannot
                           * quietly override the server just selected.
                           */
                          ...(choice.host ? { smtpHost: null, smtpPort: choice.port } : {}),
                        })
                      }
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{choice.label}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {choice.blurb}
                      </span>
                      {active && choice.userHint && (
                        <span className="mt-1 block text-xs text-navy-700">
                          Username: {choice.userHint}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <Grid>
            <Field
              label="SMTP host"
              hint={
                (form.notifications.email.relay ?? 'own') === 'custom'
                  ? 'Required for "something else".'
                  : 'Left empty, the choice above supplies it. Fill it in only to override.'
              }
            >
              <input
                className="input w-full"
                value={form.notifications.email.smtpHost ?? ''}
                onChange={(e) => patchChannel('email', { smtpHost: e.target.value })}
                placeholder={
                  RELAY_CHOICES.find((c) => c.id === (form.notifications.email.relay ?? 'own'))
                    ?.host ?? 'smtp-relay.brevo.com'
                }
              />
            </Field>
            <Field label="Port" hint="587 for STARTTLS, 465 for implicit TLS.">
              <input
                type="number"
                className="input w-full"
                value={form.notifications.email.smtpPort ?? 587}
                onChange={(e) => patchChannel('email', { smtpPort: Number(e.target.value) })}
              />
            </Field>
            <Field label="Username">
              <input
                className="input w-full"
                value={form.notifications.email.smtpUser ?? ''}
                onChange={(e) => patchChannel('email', { smtpUser: e.target.value })}
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                className="input w-full"
                value={form.notifications.email.smtpPassword ?? ''}
                onChange={(e) => patchChannel('email', { smtpPassword: e.target.value })}
              />
            </Field>
            <Field label="From address" hint="Set up SPF, DKIM and DMARC for this domain first, or it lands in spam.">
              <input
                className="input w-full"
                value={form.notifications.email.fromAddress ?? ''}
                onChange={(e) => patchChannel('email', { fromAddress: e.target.value })}
                placeholder="GoSpeedNet Dispatch <dispatch@gospeednet.co.ke>"
              />
            </Field>
            <Field label="Reply-to" hint="A monitored mailbox: technicians reply whatever the message says.">
              <input
                className="input w-full"
                value={form.notifications.email.replyTo ?? ''}
                onChange={(e) => patchChannel('email', { replyTo: e.target.value })}
              />
            </Field>
          </Grid>
          </>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-secondary"
              disabled={testEmail.isPending || form.notifications.email.transport === 'formsubmit'}
              onClick={() => testEmail.mutate()}
            >
              {testEmail.isPending ? 'Testing…' : 'Test connection'}
            </button>
            {testEmail.data && (
              <span
                className={`inline-flex items-center gap-1.5 text-sm ${
                  testEmail.data.ok ? 'text-secondary-700' : 'text-danger-700'
                }`}
              >
                {testEmail.data.ok ? (
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <XCircle className="h-4 w-4" aria-hidden="true" />
                )}
                {testEmail.data.detail}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              Save first — the test uses what is stored, not what is typed.
            </span>
          </div>

          {/*
            The button above proves the relay answers a handshake. This one
            actually sends, because those are different questions: an App
            Password can be wrong long after the connection succeeds, and
            FormSubmit accepts every send while delivering nothing until each
            recipient has confirmed their address.
          */}
          <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3">
            <p className="text-sm font-medium">Send a real test email</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Uses the saved settings and reports exactly what the provider said. The quickest way
              to find out whether an App Password is right.
            </p>

            <div className="mt-2 flex flex-wrap gap-2">
              <input
                type="email"
                className="input-field max-w-xs"
                placeholder="where to send it"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                autoComplete="email"
              />
              <button
                type="button"
                className="btn-secondary shrink-0"
                title={
                  testTo.includes('@')
                    ? `Send a test message to ${testTo.trim()}`
                    : 'Type an email address to send the test to first'
                }
                disabled={sendTest.isPending || !testTo.includes('@')}
                onClick={() => sendTest.mutate(testTo.trim())}
              >
                {sendTest.isPending ? 'Sending…' : 'Send test'}
              </button>
            </div>

            {sendTest.isError && (
              <p role="alert" className="mt-2 text-sm text-danger-700">
                {(sendTest.error as Error).message}
              </p>
            )}

            {sendTest.data && (
              <div
                className={`mt-2 rounded-lg p-2.5 text-sm ${
                  sendTest.data.ok
                    ? 'bg-secondary-50 text-secondary-700'
                    : 'bg-danger-50 text-danger-700'
                }`}
              >
                <p className="flex items-start gap-1.5 font-medium">
                  {sendTest.data.ok ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  {sendTest.data.detail}
                </p>
                {/* The fix, not just the failure. "535" and "connection
                    refused" need different actions. */}
                {sendTest.data.hint && (
                  <p className="mt-1 pl-5 text-xs opacity-90">{sendTest.data.hint}</p>
                )}
                <p className="mt-1 pl-5 text-xs opacity-70">via {sendTest.data.provider}</p>
              </div>
            )}
          </div>
        </Provider>

        <Provider
          title="WhatsApp"
          enabled={form.notifications.whatsapp.enabled}
          onToggle={(v) => patchChannel('whatsapp', { enabled: v })}
          fallback={form.environmentFallback.whatsapp}
          waiting="Needs Meta business verification and approved templates before anything can be sent."
        >
          <Grid>
            <Field label="Phone number ID" hint="From the WhatsApp Manager, not the phone number itself.">
              <input
                className="input w-full"
                value={form.notifications.whatsapp.phoneNumberId ?? ''}
                onChange={(e) => patchChannel('whatsapp', { phoneNumberId: e.target.value })}
              />
            </Field>
            <Field label="Access token">
              <input
                type="password"
                className="input w-full"
                value={form.notifications.whatsapp.accessToken ?? ''}
                onChange={(e) => patchChannel('whatsapp', { accessToken: e.target.value })}
              />
            </Field>
            <Field label="App secret" hint="Used to verify that incoming webhooks really came from Meta.">
              <input
                type="password"
                className="input w-full"
                value={form.notifications.whatsapp.appSecret ?? ''}
                onChange={(e) => patchChannel('whatsapp', { appSecret: e.target.value })}
              />
            </Field>
            <Field label="Webhook verify token">
              <input
                type="password"
                className="input w-full"
                value={form.notifications.whatsapp.webhookVerifyToken ?? ''}
                onChange={(e) => patchChannel('whatsapp', { webhookVerifyToken: e.target.value })}
              />
            </Field>
          </Grid>
        </Provider>

        <Provider
          title="SMS"
          enabled={form.notifications.sms.enabled}
          onToggle={(v) => patchChannel('sms', { enabled: v })}
          fallback={form.environmentFallback.sms}
          waiting="Needs an SMS account with credit, and the provider switched on by an administrator on the server."
        >
          <Grid>
            <Field label="Username" hint="Exactly as the provider issued it, including case.">
              <input
                className="input w-full"
                value={form.notifications.sms.username ?? ''}
                onChange={(e) => patchChannel('sms', { username: e.target.value })}
              />
            </Field>
            <Field
              label="API key"
              hint="Stored encrypted. It is never shown again once saved — paste a new one to replace it."
            >
              <input
                type="password"
                className="input w-full"
                value={form.notifications.sms.apiKey ?? ''}
                onChange={(e) => patchChannel('sms', { apiKey: e.target.value })}
              />
            </Field>
            <Field label="Sender ID" hint="The short name a customer sees. Must be registered with the provider.">
              <input
                className="input w-full"
                value={form.notifications.sms.senderId ?? ''}
                onChange={(e) => patchChannel('sms', { senderId: e.target.value })}
              />
            </Field>
          </Grid>
        </Provider>
      </Section>
    </div>
  );
}

/** Their own names for themselves, spelled the way they spell them. */
const PROVIDER_LABELS = {
  google: 'Google',
  github: 'GitHub',
  microsoft: 'Microsoft',
} as const;

/**
 * What a client id from each provider actually looks like.
 *
 * A placeholder rather than prose, because the shapes are distinctive enough
 * that seeing one is how somebody notices they have pasted the wrong field —
 * a Google client id ends in .apps.googleusercontent.com and a GitHub one does
 * not look remotely like it.
 */
const PROVIDER_ID_HINT = {
  google: '000000000000-xxxxxxxx.apps.googleusercontent.com',
  github: 'Iv1.xxxxxxxxxxxxxxxx',
  microsoft: '00000000-0000-0000-0000-000000000000',
} as const;

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card space-y-4">
      <div>
        <h3 className="font-heading text-lg">{title}</h3>
        {note && <p className="mt-0.5 text-sm text-muted-foreground">{note}</p>}
      </div>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1.5 text-sm font-medium">
        {label}
        {hint && <HelpHint>{hint}</HelpHint>}
      </span>
      {children}
    </label>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 rounded-xl bg-muted/30 p-3 text-sm">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint && <span className="block text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}

function Provider({
  title,
  live,
  waiting,
  enabled,
  fallback,
  onToggle,
  children,
}: {
  title: string;
  live?: boolean;
  waiting?: string;
  enabled: boolean;
  fallback: boolean;
  onToggle: (value: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-medium">{title}</h4>
          {live && <span className="badge bg-secondary-50 text-secondary-700">live</span>}
          {!enabled && fallback && (
            <span className="badge bg-primary-50 text-primary-700">using server configuration</span>
          )}
          {!enabled && !fallback && (
            <span className="badge bg-muted text-muted-foreground">not sending</span>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
          Configure here
        </label>
      </div>

      {waiting && <p className="mb-3 text-xs text-muted-foreground">{waiting}</p>}

      {enabled ? (
        children
      ) : (
        <p className="text-sm text-muted-foreground">
          {fallback
            ? 'Currently using the credentials set on the server. Tick to override them here.'
            : 'Nothing is sent on this channel. Tick to set up an account.'}
        </p>
      )}
    </div>
  );
}
